import * as autobind from 'autobind';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { Button, Container, Form, Message, Modal, Dimmer, Loader } from 'semantic-ui-react';
import { UsefulLinkDto, ChangeUsefulLinkDto, ChangeUsefulLinkStore } from 'stores/configuration/useful-links/useful-links-store';
import { nameof } from 'utils/object';
import RichTextEditor from 'react-rte';

interface ChangeUsefulLinkViewProps extends WithTranslation {
  onClose: (isSuccess: boolean) => void;
  changeUsefulLink?: ChangeUsefulLinkStore;
  currentUL: UsefulLinkDto;
}

// tslint:disable-next-line:no-empty-interface
interface ChangeUsefulLinkViewState {
  editorValue: any;
  userClickedSaved: boolean;
}

@connect(['changeUsefulLink', ChangeUsefulLinkStore])
class ChangeUsefulLinkView extends React.Component<ChangeUsefulLinkViewProps, ChangeUsefulLinkViewState> {
  private get changeUsefulLinkStore() {
    return this.props.changeUsefulLink;
  }

  constructor(props: ChangeUsefulLinkViewProps) {
    super(props);

    this.state = {
      editorValue: RichTextEditor.createEmptyValue(),
      userClickedSaved: false
    };

    this.changeUsefulLinkStore.state.result = null;

    this.changeUsefulLinkStore.change({
      id: this.props.currentUL.id,
      name: this.props.currentUL.name,
      link: this.props.currentUL.link,
      role: this.props.currentUL.role,
      active: this.props.currentUL.active
    });
  }

  componentDidMount() {
    const editorValue = RichTextEditor.createValueFromString(this.props.currentUL.link, 'html');

    this.setState({ editorValue });
  }

  UNSAFE_componentWillReceiveProps(nextProps: ChangeUsefulLinkViewProps) {
    if (this.changeUsefulLinkStore.state.result && this.changeUsefulLinkStore.state.result.isSuccess) {
      nextProps.onClose(true);
    }
  }

  handleEditorChanges = editorValue => {
    this.setState({ editorValue });
    this.handleValue(nameof<ChangeUsefulLinkDto>('link'), editorValue.toString('html'));
  };

  @autobind
  private onChangeItem() {
    this.setState({ userClickedSaved: true });
    this.changeUsefulLinkStore.update();
  }

  @autobind
  private onCancelChangeItem() {
    this.changeUsefulLinkStore.clear();
    this.props.onClose(false);
  }

  @autobind
  private handleValue(property: string, value: any) {
    this.setState({ userClickedSaved: false });
    const change = {};
    change[property] = value;
    this.changeUsefulLinkStore.change({ ...this.changeUsefulLinkStore.state.item, ...change });
  }

  public render() {
    const { t } = this.props as any;
    const roleOptions = [
      { key: 'e', text: t('Employee'), value: 'Employee' },
      { key: 'p', text: t('PoC'), value: 'PoC' },
      { key: 'i', text: t('Instructor'), value: 'Instructor' }
    ];

    return (
      <Modal open closeOnEscape={true} onClose={this.onCancelChangeItem} closeOnDimmerClick={false}>
        <Dimmer active={this.changeUsefulLinkStore.state.isBusy} style={{ background: 'rgba(0, 0, 0, 0.4)' }}>
          <Loader indeterminate>{t('')}</Loader>
        </Dimmer>
        <Modal.Header className="modal__header">{t('Edit Useful Link')}</Modal.Header>
        <Modal.Content image>
          <Container>
            <Dimmer active={this.changeUsefulLinkStore.state.isBusy} inverted>
              <Loader size="large" indeterminate inverted></Loader>
            </Dimmer>
            {this.changeUsefulLinkStore.state.result && !this.changeUsefulLinkStore.state.result.isSuccess && (
              <Message
                className="error-message__style"
                icon="exclamation circle"
                error
                header={t('An error ocurred')}
                list={this.changeUsefulLinkStore.state.result.messages.map(o => o.body)}
              />
            )}
            {this.changeUsefulLinkStore.state.item && (
              <Form>
                <Form.Group widths="equal" className="useful-link-form-group">
                  <Form.Input
                    required
                    fluid
                    error={!this.changeUsefulLinkStore.state.item.name && this.state.userClickedSaved}
                    label={t('Link')}
                    placeholder={t('Name')}
                    value={this.changeUsefulLinkStore.state.item.name}
                    onChange={(e, { value }) => this.handleValue(nameof<ChangeUsefulLinkDto>('name'), value)}
                  />
                  <Form.Select
                    required
                    fluid
                    error={!this.changeUsefulLinkStore.state.item.role && this.state.userClickedSaved}
                    label={t('Role')}
                    placeholder={t('Role')}
                    options={roleOptions}
                    value={this.changeUsefulLinkStore.state.item.role}
                    onChange={(e, { value }) => this.handleValue(nameof<ChangeUsefulLinkDto>('role'), value)}
                  />
                  <Form.Checkbox
                    toggle
                    className="custom-check-box custom-position"
                    checked={this.changeUsefulLinkStore.state.item.active}
                    label={t('Active')}
                    placeholder={t('Active')}
                    onChange={(e, { checked }) => this.handleValue(nameof<ChangeUsefulLinkDto>('active'), checked)}
                  />
                </Form.Group>
              </Form>
            )}
            <br />
            <RichTextEditor className="custom-rte rte-30vh" value={this.state.editorValue} onChange={this.handleEditorChanges} />
          </Container>
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={this.onCancelChangeItem}>
            {t('Cancel')}
          </Button>
          <Button onClick={this.onChangeItem} positive>
            {t('Save')}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

// Wire up the React component to the Redux store
export default withTranslation()(ChangeUsefulLinkView);
